import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDeleteDraftBulkUpload, useMyBulkListingDrafts } from '@steelbuy/data-access';
import { useModal } from '@steelbuy/modal-dialog';
import { BulkUploadMyDraftModel } from '@steelbuy/ts-shared';
import { BulkDraftTeaser } from '@steelbuy/ui-domain';
import {
    DataHeader,
    FillHeightContainer,
    LoadingSpinner,
    Card,
    ButtonTertiaryOnLightM,
    LoadingStatus,
    useSuccessErrorNotification,
    ConfirmCancelModel,
} from '@steelbuy/ui-primitive';
import { createRouteUrl, RoutePath } from '../../router/Routes';
import { FOOTER_HEIGHT } from '../app-layout/app-footer/AppFooter';
import { MyListingEmptyState } from '../my-listings/MyListingEmptyState';

export const MyBulkListingDrafts = () => {
    const { t } = useTranslation(['translation', 'uiDomain']);
    const modalAccessor = useModal();
    const {
        data: drafts,
        isLoading,
        total,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        refetch,
    } = useMyBulkListingDrafts({});
    const { Notification, showSuccessNotification, showErrorNotification, hideNotification } =
        useSuccessErrorNotification();
    const [selectedDraft, setSelectedDraft] = useState<BulkUploadMyDraftModel | null>(null);
    const { mutate, isLoading: isPending } = useDeleteDraftBulkUpload();
    const navigate = useNavigate();

    const goBackLink = { goBackRoute: `${RoutePath.MY_LISTINGS}#drafts-bulkDrafts` };

    const handleLoadMore = () => {
        fetchNextPage();
    };

    const handleDeleteDraft = (draft: BulkUploadMyDraftModel) => {
        modalAccessor.show();
        setSelectedDraft(draft);
    };

    const handleListingDraftDelete = (draftId: string) => {
        hideNotification();
        modalAccessor.setActionPending(true);
        mutate(draftId, {
            onSuccess: () => {
                showSuccessNotification(t('application.createListingOverview.notificationSuccess'));
                modalAccessor.hide();
                modalAccessor.setActionPending(false);
                refetch();
            },
            onError: () => {
                showErrorNotification(t('application.createListingOverview.notificationError'));
                modalAccessor.hide();
                modalAccessor.setActionPending(false);
            },
        });
    };

    return (
        <div className="my-listings-list">
            <DataHeader
                headingValue={total !== undefined ? t('application.myListings.numberOfDrafts', { count: total }) : ''}
            />
            {isLoading ? (
                <FillHeightContainer extraPadding={FOOTER_HEIGHT}>
                    <LoadingSpinner fillContainerHeight />
                </FillHeightContainer>
            ) : (
                <>
                    {Notification}
                    <div className="my-listings-list__listings">
                        {drafts?.map((draft) => (
                            <Card
                                isClickable
                                key={draft.bulkId}
                                url={createRouteUrl(RoutePath.CREATE_BULK_LISTING_EDIT_LISTING_DRAFT, [
                                    'draftId',
                                    draft.bulkId,
                                ])}
                                linkState={goBackLink}
                            >
                                <BulkDraftTeaser
                                    draft={draft}
                                    onDelete={handleDeleteDraft}
                                    onEdit={() => {
                                        navigate(
                                            createRouteUrl(RoutePath.CREATE_BULK_LISTING_EDIT_LISTING_DRAFT, [
                                                'draftId',
                                                draft.bulkId,
                                            ]),
                                            { state: goBackLink }
                                        );
                                    }}
                                />
                            </Card>
                        ))}
                    </div>
                    {hasNextPage && (
                        <div className="my-listings-list__load-more">
                            <ButtonTertiaryOnLightM
                                onClick={handleLoadMore}
                                label={t('uiDomain:commonList.loadMore')}
                                loadingStatus={isFetchingNextPage ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                            />
                        </div>
                    )}
                </>
            )}
            {total === 0 && <MyListingEmptyState navigateTo={RoutePath.CREATE_BULK_LISTING_WIZARD} />}
            <ConfirmCancelModel
                modalAccessor={modalAccessor}
                cancelLabel={t('uiDomain:common.cancel')}
                header={t('application.myBulkDrafts.deleteModal.header', {
                    count: selectedDraft?.listingDraftCount,
                })}
                onClose={() => {}}
                onSubmit={() => {
                    selectedDraft && handleListingDraftDelete(selectedDraft.bulkId);
                }}
                isDanger
                submitLabel={t('application.myBulkDrafts.deleteModal.deleteCta')}
                isSubmitting={isPending}
            />
        </div>
    );
};
